import React, { useMemo, useState } from 'react';

import {
    Card,
    Empty,
    Pagination,
    Space,
    Spin,
    Tag,
    Typography
} from 'antd';

import dayjs from 'dayjs';

import './TaskManagerList.css';
import './HrDealsList.css';

const { Text, Title } = Typography;

const StageTag = ({ name, color }) => (
    <Tag
        style={{
            margin: 0,
            color: '#fff',
            background: color || '#1677ff',
            borderColor: color || '#1677ff',
            fontWeight: 600
        }}
    >
        {name || 'Без стадии'}
    </Tag>
);

const MetaItem = ({ label, value }) => (
    <div className="tm-list-meta-item">
        <Text type="secondary" className="tm-list-meta-label">
            {label}
        </Text>
        <Text className="tm-list-meta-value" ellipsis>
            {value || '—'}
        </Text>
    </div>
);

const formatWhen = value => {
    if (!value) return '—';
    const d = dayjs(value);
    return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
};

const HrDealsList = ({
    deals = [],
    loading = false,
    onDealClick
}) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const sortedDeals = useMemo(
        () =>
            [...deals].sort((a, b) => {
                const ta = a.date_modify || a.date_create || '';
                const tb = b.date_modify || b.date_create || '';
                if (ta === tb) {
                    return Number(b.id) - Number(a.id);
                }
                return String(tb).localeCompare(String(ta));
            }),
        [deals]
    );

    const maxPage = Math.max(
        1,
        Math.ceil(sortedDeals.length / pageSize) || 1
    );
    const safePage = Math.min(page, maxPage);

    const pageDeals = useMemo(() => {
        const start = (safePage - 1) * pageSize;
        return sortedDeals.slice(start, start + pageSize);
    }, [sortedDeals, safePage, pageSize]);

    if (!deals.length && !loading) {
        return <Empty description="Ничего не найдено" />;
    }

    return (
        <Spin spinning={loading} wrapperClassName="tm-list-spin">
            <div className="tm-list hr-deals-list">
                <div className="tm-list-stack">
                    {pageDeals.map(deal => (
                        <Card
                            key={deal.id}
                            size="small"
                            hoverable
                            className="tm-list-card"
                            onClick={() => onDealClick?.(deal)}
                            style={{
                                borderLeft: `3px solid ${
                                    deal.stage_color || '#1677ff'
                                }`
                            }}
                        >
                            <div className="tm-list-card-head">
                                <Space wrap size={[8, 6]}>
                                    <Text type="secondary" strong>
                                        #{deal.id}
                                    </Text>
                                    <StageTag
                                        name={deal.stage_name}
                                        color={deal.stage_color}
                                    />
                                </Space>
                                <Text type="secondary">
                                    {formatWhen(
                                        deal.date_modify ||
                                            deal.date_create
                                    )}
                                </Text>
                            </div>

                            <Title
                                level={5}
                                className="tm-list-card-title"
                            >
                                {deal.title ||
                                    deal.contact_name ||
                                    'Без названия'}
                            </Title>

                            <div className="tm-list-meta-grid">
                                <MetaItem
                                    label="Кандидат"
                                    value={deal.contact_name}
                                />
                                <MetaItem
                                    label="Телефон"
                                    value={deal.contact_phone}
                                />
                                <MetaItem
                                    label="Ответственный"
                                    value={deal.assigned_name}
                                />
                                <MetaItem
                                    label="Город"
                                    value={deal.city}
                                />
                                <MetaItem
                                    label="Вакансия"
                                    value={deal.vacancy_name}
                                />
                                <MetaItem
                                    label="Источник"
                                    value={deal.source_name}
                                />
                            </div>
                        </Card>
                    ))}
                </div>

                {sortedDeals.length > 0 ? (
                    <div className="tm-list-pagination">
                        <Pagination
                            current={safePage}
                            pageSize={pageSize}
                            total={sortedDeals.length}
                            showSizeChanger
                            pageSizeOptions={[
                                '25',
                                '50',
                                '100',
                                '200'
                            ]}
                            showTotal={totalCount => (
                                <Text type="secondary">
                                    {totalCount.toLocaleString(
                                        'ru-RU'
                                    )}{' '}
                                    записей
                                </Text>
                            )}
                            onChange={(nextPage, nextSize) => {
                                setPage(nextPage);
                                setPageSize(nextSize);
                            }}
                        />
                    </div>
                ) : null}
            </div>
        </Spin>
    );
};

export default HrDealsList;
